import { useCallback, useState } from 'react';
import i18next from '@/i18n';
import { useQueryClient } from '@tanstack/react-query';
import type { ImportMember } from '@sharecost/shared';
import {
  parseSpreadsheet,
  resolveRows,
  detectImportMapping,
  buildImportPreview,
  commitImport,
  fetchImportCategories,
  type ImportPreview,
  type CommitResult,
} from '@/api/import';

export type ImportStage =
  | 'idle'
  | 'parsing'
  | 'detecting'
  | 'preview'
  | 'committing'
  | 'done'
  | 'error';

interface UseImportArgs {
  groupId: string;
  groupCurrency: string;
  createdBy: string;
  members: ImportMember[];
}

export function useImport({ groupId, groupCurrency, createdBy, members }: UseImportArgs) {
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<ImportStage>('idle');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<CommitResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(
    async (file: File) => {
      setError(null);
      setResult(null);
      setFileName(file.name);
      try {
        setStage('parsing');
        const matrix = await parseSpreadsheet(file);
        if (matrix.length === 0) {
          throw new Error(i18next.t('web:importExpensesPage.fileEmpty'));
        }

        setStage('detecting');
        const mapping = await detectImportMapping(matrix, members);
        const parsed = resolveRows(matrix, mapping.hasHeader);
        const categories = await fetchImportCategories();
        const prev = await buildImportPreview(parsed, mapping, members, groupId, categories);
        setPreview(prev);
        setStage('preview');
      } catch (e) {
        setError(e instanceof Error ? e.message : i18next.t('web:importExpensesPage.failedToReadFile'));
        setStage('error');
      }
    },
    [groupId, members],
  );

  const setTokenVirtual = useCallback((token: string, createVirtual: boolean) => {
    setPreview((p) =>
      p
        ? {
            ...p,
            tokens: p.tokens.map((t) =>
              t.token === token ? { ...t, createVirtual } : t,
            ),
          }
        : p,
    );
  }, []);

  const toggleDraft = useCallback((index: number) => {
    setPreview((p) =>
      p
        ? {
            ...p,
            drafts: p.drafts.map((d) =>
              d.index === index ? { ...d, include: !d.include } : d,
            ),
          }
        : p,
    );
  }, []);

  const commit = useCallback(async () => {
    if (!preview) return;
    const total = preview.drafts.filter((d) => d.include).length;
    setProgress({ done: 0, total });
    setStage('committing');
    try {
      const res = await commitImport(
        preview,
        { groupId, createdBy, groupCurrency, members },
        (done, t) => setProgress({ done, total: t }),
      );
      setResult(res);
      setStage('done');
      // Group detail holds the member list — refetch it so newly created virtual
      // members resolve in balances/members instead of showing as "unknown".
      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      queryClient.invalidateQueries({ queryKey: ['expenses', groupId] });
      queryClient.invalidateQueries({ queryKey: ['balances', groupId] });
      // Dashboard group cards (expense count, balance, weekly-activity sparkline).
      queryClient.invalidateQueries({ queryKey: ['dashboard-balances'] });
      queryClient.invalidateQueries({ queryKey: ['settlements', groupId] });
      queryClient.invalidateQueries({ queryKey: ['groupBudget', groupId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-budgets'] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      // Statistics are derived from expenses/settlements — refresh them too,
      // otherwise the group stats charts show pre-import data until a refresh.
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
      // Refresh activity feeds (dashboard recent + activities page) so the
      // summary "imported" entry shows without a manual page refresh.
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : i18next.t('web:importExpensesPage.importFailed'));
      setStage('error');
    }
  }, [preview, groupId, createdBy, groupCurrency, members, queryClient]);

  const reset = useCallback(() => {
    setStage('idle');
    setPreview(null);
    setFileName(null);
    setResult(null);
    setError(null);
    setProgress({ done: 0, total: 0 });
  }, []);

  return {
    stage,
    preview,
    fileName,
    progress,
    result,
    error,
    start,
    commit,
    reset,
    setTokenVirtual,
    toggleDraft,
  };
}
